import { useCallback, useEffect, useRef, useState } from 'react';
import { of, ReplaySubject } from 'rxjs';
import { catchError, debounceTime, filter, switchMap } from 'rxjs/operators';

import { getProductSearchResults } from '../../store/products/products.api';

export type ProductSearchResult = {
  productId: string;
  name: string;
  sku: string;
  thumbnailUrl: string | null;
  dangerousGoods?: boolean;
  isBundle?: boolean;
};

const MINIMUM_SEARCH_INPUT_LENGTH = 3;
const emptyResults: ProductSearchResult[] = [];

export const useProductTypeAheadSearch = () => {
  const [results, setResults] = useState<ProductSearchResult[]>(emptyResults);
  const replaySubject = useRef(new ReplaySubject<string>());

  useEffect(() => {
    const searchResult$ = replaySubject.current.pipe(
      debounceTime(500),
      filter((value) => value?.length >= MINIMUM_SEARCH_INPUT_LENGTH),
      switchMap((value) => getProductSearchResults(value).pipe(catchError(() => of([])))),
    );

    const subscription = searchResult$.subscribe((newResults: ProductSearchResult[]) => setResults(newResults));

    return () => subscription.unsubscribe();
  }, []);

  const clearResults = useCallback(() => setResults(emptyResults), []);

  const onSearchChange = useCallback((value: string) => {
    if (value?.length >= MINIMUM_SEARCH_INPUT_LENGTH) {
      replaySubject.current.next(value);
    } else {
      setResults(emptyResults);
    }
  }, []);

  return { results, clearResults, onSearchChange };
};
